/**
 * Shrinks a phone photo before upload. A 12-megapixel camera shot is 3–6 MB;
 * a delivery slip stays readable at 1600 px on the long edge as a JPEG of a
 * few hundred KB, which is what the tenant database stores (cap: 1.5 MB).
 */

export const PHOTO_MAX_EDGE = 1600
export const PHOTO_MAX_BYTES = 1_572_864

/** Target size keeping the aspect ratio, never upscaling. */
export function fitWithin(width: number, height: number, maxEdge = PHOTO_MAX_EDGE): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 }
  const scale = Math.min(1, maxEdge / Math.max(width, height))
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/** Base64 payload of a data URL ("data:image/jpeg;base64,XXXX" → "XXXX"). */
export function base64OfDataUrl(dataUrl: string): string {
  const i = dataUrl.indexOf(',')
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl
}

/** Bytes a base64 string decodes to. */
export function base64Bytes(b64: string): number {
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0
  return Math.floor((b64.length * 3) / 4) - pad
}

/** Resizes and re-encodes a picked image as JPEG, lowering quality if needed. */
export async function shrinkPhoto(file: File): Promise<{ content_type: 'image/jpeg'; data_base64: string }> {
  const bitmap = await createImageBitmap(file)
  const { width, height } = fitWithin(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This device cannot prepare the photo')
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()
  for (const quality of [0.8, 0.65, 0.5]) {
    const b64 = base64OfDataUrl(canvas.toDataURL('image/jpeg', quality))
    if (base64Bytes(b64) <= PHOTO_MAX_BYTES) return { content_type: 'image/jpeg', data_base64: b64 }
  }
  throw new Error('The photo is too big even after shrinking')
}
